import {
  CheckCircleFilled,
  ExportOutlined,
  KeyOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Drawer, Flex, Segmented, Skeleton, Switch, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/client';
import { keys, useAccountSecurity, useApiMutation } from '../api/hooks';
import { useAuth, type AccountAction } from '../auth/AuthProvider';
import { status } from '../theme';
import type { NotificationPreferences } from '../api/types';
import { accountConsoleUrl } from '../auth/oidc';
import { useSiteContext } from '../context/SiteContext';
import { LANGUAGES, setLocale, type AppLocale } from '../i18n';
import { UserAvatar } from '../components/UserAvatar';

export { LANGUAGES };

/** Saves the language on the profile, so e-mails and the next device use it too (FR-TRV-16). */
export function useSaveLocale() {
  const api = useApi();
  const qc = useQueryClient();
  return (locale: AppLocale) => {
    setLocale(locale);
    // quietly: the interface already switched, the profile follows
    void api('/me/preferences', { method: 'PATCH', body: JSON.stringify({ locale }) })
      .then(() => qc.invalidateQueries({ queryKey: keys.me }))
      .catch(() => undefined);
  };
}

/**
 * The signed-in user's profile (FR-TRV-16): language, notification channels, and a link to the
 * Keycloak account console for the password, second factor, sessions and devices.
 */
export function ProfileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const { me, sites } = useSiteContext();
  const saveLocale = useSaveLocale();
  const notifications = me?.principal.notifications ?? { email: true, dailyDigest: true };
  const save = useApiMutation(
    (n: Partial<NotificationPreferences>) =>
      api('/me/preferences', { method: 'PATCH', body: JSON.stringify({ notifications: n }) }),
    { invalidate: [keys.me], success: t('profile.saved') },
  );
  const name = me?.principal.displayName ?? '';

  return (
    <Drawer open={open} onClose={onClose} size={460} title={t('profile.title')}>
      <Flex vertical gap={16}>
        <Flex gap={14} align="center">
          <UserAvatar name={name || '?'} size={52} />
          <div style={{ minWidth: 0 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {name}
            </Typography.Title>
            <Typography.Text type="secondary">{me?.principal.email}</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {me?.tenant.name}
              </Typography.Text>
            </div>
          </div>
        </Flex>

        <Card size="small" title={t('profile.access')}>
          {sites.length === 0 ? (
            <Typography.Text type="secondary">{t('users.noAccess')}</Typography.Text>
          ) : (
            <Flex vertical gap={6}>
              {sites.map((s) => (
                <Flex key={s.id} justify="space-between" gap={8} wrap>
                  <span>{s.name}</span>
                  <span>
                    {s.roles.map((r) => (
                      <Tag key={r} color="cyan" style={{ marginInlineEnd: 4 }}>
                        {t(`roles.${r}`)}
                      </Tag>
                    ))}
                  </span>
                </Flex>
              ))}
            </Flex>
          )}
        </Card>

        <Card size="small" title={t('profile.language')}>
          <Segmented
            block
            value={i18n.language.slice(0, 2)}
            onChange={(v) => saveLocale(v as AppLocale)}
            options={LANGUAGES}
          />
          <Typography.Paragraph type="secondary" style={{ margin: '8px 0 0', fontSize: 12 }}>
            {t('profile.languageHint')}
          </Typography.Paragraph>
        </Card>

        <Card size="small" title={t('profile.notifications')}>
          <Flex vertical gap={14}>
            {(['email', 'dailyDigest'] as const).map((k) => (
              <Flex key={k} justify="space-between" align="flex-start" gap={12}>
                <div>
                  <div style={{ fontWeight: 550 }}>{t(`profile.channels.${k}`)}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`profile.channels.${k}Hint`)}
                  </Typography.Text>
                </div>
                <Switch
                  checked={notifications[k]}
                  loading={save.isPending}
                  onChange={(checked) => save.mutate({ [k]: checked })}
                />
              </Flex>
            ))}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('profile.regulatoryAlerts')}
            </Typography.Text>
          </Flex>
        </Card>

        <SecurityCard />
      </Flex>
    </Drawer>
  );
}

function FactorRow(props: {
  icon: ReactNode;
  title: string;
  hint: string;
  state: string | null;
  done: boolean;
  action: AccountAction;
  label: string;
}) {
  const { startAccountAction } = useAuth();
  return (
    <Flex gap={12} align="flex-start">
      <span style={{ fontSize: 18, lineHeight: '22px', flex: 'none' }}>{props.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Flex gap={8} align="center" wrap>
          <span style={{ fontWeight: 600 }}>{props.title}</span>
          {props.state && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {props.done && (
                <CheckCircleFilled style={{ color: status.success, marginInlineEnd: 4 }} />
              )}
              {props.state}
            </Typography.Text>
          )}
        </Flex>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {props.hint}
        </Typography.Text>
      </div>
      <Button size="small" onClick={() => void startAccountAction(props.action)}>
        {props.label}
      </Button>
    </Flex>
  );
}

/**
 * Two-step verification and the second factors of the signed-in user (ADR-0006). Each button
 * opens the Keycloak page that sets the factor up and comes back here; removing one, sessions and
 * devices stay in the Keycloak account console.
 */
function SecurityCard() {
  const { t, i18n } = useTranslation();
  const security = useAccountSecurity();
  const twoStep = security.data?.twoStep;
  const f = security.data?.factors;
  const count = (n: number | undefined) =>
    n ? t('profile.factorCount', { count: n }) : f ? t('profile.notSetUp') : null;
  return (
    <Card size="small" title={t('profile.security')}>
      <Flex vertical gap={16}>
        {security.isLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : (
          <>
            <Flex gap={8} align="center" wrap>
              <SafetyCertificateOutlined />
              <span style={{ fontWeight: 600 }}>{t('profile.twoStep')}</span>
              <Tag color={twoStep?.required ? 'gold' : undefined} style={{ marginInlineEnd: 0 }}>
                {twoStep?.required ? t('profile.twoStepRequired') : t('profile.twoStepOptional')}
              </Tag>
            </Flex>
            <FactorRow
              icon={<MobileOutlined />}
              title={t('profile.authenticator')}
              hint={t('profile.authenticatorHint')}
              state={count(f?.otp)}
              done={Boolean(f?.otp)}
              action="CONFIGURE_TOTP"
              label={f?.otp ? t('profile.addAnother') : t('profile.setUp')}
            />
            <FactorRow
              icon={<KeyOutlined />}
              title={t('profile.passkeys')}
              hint={t('profile.passkeysHint')}
              state={count(f?.passkeys)}
              done={Boolean(f?.passkeys)}
              action="webauthn-register-passwordless"
              label={t('profile.add')}
            />
            <FactorRow
              icon={<SolutionOutlined />}
              title={t('profile.recoveryCodes')}
              hint={t('profile.recoveryCodesHint')}
              state={
                f ? (f.recoveryCodes ? t('profile.recoveryReady') : t('profile.notSetUp')) : null
              }
              done={Boolean(f?.recoveryCodes)}
              action="CONFIGURE_RECOVERY_AUTHN_CODES"
              label={f?.recoveryCodes ? t('profile.regenerate') : t('profile.generate')}
            />
            <FactorRow
              icon={<LockOutlined />}
              title={t('profile.password')}
              hint={t('profile.passwordHint')}
              state={null}
              done={false}
              action="UPDATE_PASSWORD"
              label={t('profile.change')}
            />
          </>
        )}
        <div>
          <Typography.Paragraph type="secondary" style={{ margin: '0 0 8px', fontSize: 12 }}>
            {t('profile.securityHint')}
          </Typography.Paragraph>
          <Button
            icon={<SafetyCertificateOutlined />}
            href={accountConsoleUrl(i18n.language.slice(0, 2))}
            target="_blank"
            rel="noreferrer"
          >
            {t('profile.manageAccount')} <ExportOutlined />
          </Button>
        </div>
      </Flex>
    </Card>
  );
}
